import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, FileSignature, CheckCircle, Clock, RefreshCw, AlertCircle, DollarSign } from "lucide-react";

const statusColors = {
  pending_signatures: "bg-amber-50 text-amber-700",
  active: "bg-emerald-50 text-emerald-700",
  expired: "bg-gray-100 text-gray-500",
  terminated: "bg-red-50 text-red-700",
  renewed: "bg-blue-50 text-blue-700",
};

const paymentStatusColors = {
  pending: "bg-amber-50 text-amber-700",
  paid: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700",
};

export default function TenantLeasesPage() {
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [signDialog, setSignDialog] = useState(null);
  const [signing, setSigning] = useState(false);
  const [leasePayments, setLeasePayments] = useState({});

  const fetchLeases = async () => {
    setError("");
    try {
      const res = await api.get("/tenant/leases");
      setLeases(res.data);
      // Fetch payments for each lease
      const paymentsMap = {};
      for (const lease of res.data) {
        try {
          const pRes = await api.get(`/payments?lease_id=${lease._id}`);
          paymentsMap[lease._id] = pRes.data;
        } catch { paymentsMap[lease._id] = []; }
      }
      setLeasePayments(paymentsMap);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Failed to load leases. Please try refreshing the page.");
    }
    setLoading(false);
  };

  useEffect(() => { fetchLeases(); }, []);

  const handleSign = async () => {
    setSigning(true);
    try {
      await api.post(`/tenant/leases/${signDialog._id}/sign`);
      toast.success("Lease signed successfully!");
      setSignDialog(null);
      fetchLeases();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSigning(false);
    }
  };

  const handleRenewal = async (leaseId) => {
    try {
      await api.post(`/tenant/leases/${leaseId}/renew`);
      toast.success("Renewal request submitted");
      fetchLeases();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="My Leases">
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Leases">
      <div className="space-y-6" data-testid="tenant-leases-page">
        <p className="text-sm text-muted-foreground">View your lease agreements, sign pending leases, and track associated charges.</p>

        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <p className="text-sm">{error}</p>
                <Button size="sm" variant="outline" className="ml-auto text-xs" onClick={() => { setLoading(true); fetchLeases(); }}>Retry</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {!error && leases.length === 0 ? (
          <Card className="border-border/60">
            <CardContent className="text-center py-12">
              <FileSignature className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No leases found. Leases appear here after your application is approved and a lease is generated.</p>
            </CardContent>
          </Card>
        ) : (
          leases.map((lease) => {
            const payments = leasePayments[lease._id] || [];
            const totalOwed = payments.filter(p => p.status === "pending").reduce((s, p) => s + p.amount, 0);
            const totalPaid = payments.filter(p => p.status === "paid").reduce((s, p) => s + p.amount, 0);

            const canSign = lease.status === "pending_signatures" && !lease.tenant_signed_at && lease.tenant_signed_at !== "None";

            return (
              <Card key={lease._id} className="border-border/60" data-testid={`lease-card-${lease._id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="font-heading text-lg">Room {lease.room_number} — Lease Agreement</CardTitle>
                    <Badge className={statusColors[lease.status]}>{lease.status?.replace(/_/g, " ")}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Key Details */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                    <div><span className="text-muted-foreground block">Start Date</span><span className="font-medium">{lease.start_date}</span></div>
                    <div><span className="text-muted-foreground block">End Date</span><span className="font-medium">{lease.end_date}</span></div>
                    <div><span className="text-muted-foreground block">Monthly Rent</span><span className="font-medium">${lease.rent_amount}/mo</span></div>
                    <div><span className="text-muted-foreground block">Security Deposit</span><span className="font-medium">${lease.deposit}</span></div>
                  </div>

                  <Separator />

                  {/* Signing Status */}
                  <div>
                    <h4 className="font-semibold text-sm mb-3">Signature Status</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className={`flex items-center gap-2 p-3 rounded-lg border text-sm ${lease.tenant_signed_at ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
                        {lease.tenant_signed_at
                          ? <><CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" /> <span>Tenant signed on {new Date(lease.tenant_signed_at).toLocaleDateString()}</span></>
                          : <><Clock className="h-4 w-4 text-amber-600 shrink-0" /> <span>Awaiting your signature</span></>
                        }
                      </div>
                      <div className={`flex items-center gap-2 p-3 rounded-lg border text-sm ${lease.admin_signed_at ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
                        {lease.admin_signed_at
                          ? <><CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" /> <span>Landlord signed on {new Date(lease.admin_signed_at).toLocaleDateString()}</span></>
                          : <><Clock className="h-4 w-4 text-amber-600 shrink-0" /> <span>Awaiting landlord signature</span></>
                        }
                      </div>
                    </div>
                    {lease.status === "pending_signatures" && !lease.tenant_signed_at && !lease.admin_signed_at && (
                      <div className="flex items-center gap-2 mt-3 text-sm text-amber-700">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        Both tenant and landlord signatures are required to activate this lease.
                      </div>
                    )}
                  </div>

                  {/* Lease Charges */}
                  {payments.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                          <DollarSign className="h-4 w-4" /> Lease Charges
                        </h4>
                        <div className="rounded-lg border overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Description</TableHead>
                                <TableHead>Reference</TableHead>
                                <TableHead>Amount</TableHead>
                                <TableHead>Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {payments.map((p) => (
                                <TableRow key={p._id}>
                                  <TableCell className="capitalize font-medium">{p.type} {p.admin_notes ? `— ${p.admin_notes}` : ""}</TableCell>
                                  <TableCell className="font-mono text-xs">{p.reference}</TableCell>
                                  <TableCell className="font-semibold">${p.amount.toFixed(2)}</TableCell>
                                  <TableCell><Badge className={paymentStatusColors[p.status]}>{p.status}</Badge></TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                        <div className="flex gap-6 mt-3 text-sm">
                          {totalOwed > 0 && <span className="text-amber-700 font-semibold">Outstanding: ${totalOwed.toFixed(2)}</span>}
                          {totalPaid > 0 && <span className="text-emerald-700 font-semibold">Paid: ${totalPaid.toFixed(2)}</span>}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    {lease.status === "pending_signatures" && !lease.tenant_signed_at && (
                      <Button className="bg-[#E25822] hover:bg-[#E25822]/90 text-white" onClick={() => setSignDialog(lease)} data-testid={`sign-lease-${lease._id}`}>
                        <FileSignature className="h-4 w-4 mr-2" /> Review & Sign Lease
                      </Button>
                    )}
                    {lease.status === "active" && !lease.renewal_requested && (
                      <Button variant="outline" onClick={() => handleRenewal(lease._id)} data-testid={`renew-lease-${lease._id}`}>
                        <RefreshCw className="h-4 w-4 mr-2" /> Request Renewal
                      </Button>
                    )}
                    {lease.renewal_requested && (
                      <div className="flex items-center gap-2 text-sm text-blue-600">
                        <Clock className="h-4 w-4" /> Renewal requested — awaiting response
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}

        {/* Sign Dialog — Full Lease View */}
        <Dialog open={!!signDialog} onOpenChange={(o) => !o && setSignDialog(null)}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col" data-testid="sign-lease-dialog">
            <DialogHeader className="shrink-0">
              <DialogTitle className="font-heading text-xl">Lease Agreement — Room {signDialog?.room_number}</DialogTitle>
            </DialogHeader>
            {signDialog && (
              <div className="flex-1 overflow-y-auto space-y-4 min-h-0">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm bg-muted p-4 rounded-lg">
                  <div><span className="text-muted-foreground block">Rent</span><span className="font-semibold">${signDialog.rent_amount}/mo</span></div>
                  <div><span className="text-muted-foreground block">Deposit</span><span className="font-semibold">${signDialog.deposit}</span></div>
                  <div><span className="text-muted-foreground block">Period</span><span className="font-semibold">{signDialog.start_date} to {signDialog.end_date}</span></div>
                  <div><span className="text-muted-foreground block">Category</span><span className="font-semibold capitalize">{signDialog.room_category}</span></div>
                </div>

                <div className="border rounded-lg p-4 bg-white">
                  <pre className="text-xs leading-relaxed whitespace-pre-wrap font-body text-[#0F172A]">{signDialog.terms}</pre>
                </div>

                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <strong>Important:</strong> By clicking "I Accept & Sign" below, you electronically sign this lease agreement and agree to all terms and conditions stated above. Both your signature and the landlord's signature are required for the lease to become active.
                </div>
              </div>
            )}
            <DialogFooter className="shrink-0 border-t pt-4 mt-2">
              <Button variant="outline" onClick={() => setSignDialog(null)}>Cancel</Button>
              <Button className="bg-[#E25822] hover:bg-[#E25822]/90 text-white" onClick={handleSign} disabled={signing} data-testid="confirm-sign-btn">
                {signing ? <Loader2 className="h-4 w-4 animate-spin" /> : "I Accept & Sign"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
